import React, { forwardRef } from 'react';

export type ButtonVariant =
  | 'primary'
  | 'spark'
  | 'ghost'
  | 'destructive'
  | 'partner'
  | 'subtle';

export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: 'var(--accent)',
    color: '#fff',
    border: '1px solid var(--accent)',
  },
  spark: {
    background: 'var(--accent-2)',
    color: 'var(--cg-midnight-900)',
    border: '1px solid var(--accent-2)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-2)',
  },
  destructive: {
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    border: '1px solid var(--cg-terra-200)',
  },
  partner: {
    background: 'var(--partner-soft)',
    color: 'var(--cg-plum-600)',
    border: '1px solid transparent',
  },
  subtle: {
    background: 'var(--accent-soft)',
    color: 'var(--accent-soft-fg)',
    border: '1px solid transparent',
  },
};

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: { height: 28, padding: '0 10px', fontSize: 12.5, gap: 6 },
  md: { height: 36, padding: '0 14px', fontSize: 13.5, gap: 8 },
  lg: { height: 44, padding: '0 18px', fontSize: 14.5, gap: 10 },
};

/**
 * The button look as a style object, for elements that must stay what they
 * are — e.g. an `<a href>` that opens a page (a `<button>` inside an `<a>` is
 * invalid HTML and gives two tab stops).
 */
export function buttonStyles(variant: ButtonVariant = 'primary', size: ButtonSize = 'md'): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-sans)',
    fontWeight: 600,
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    textDecoration: 'none',
    boxShadow: variant === 'primary' || variant === 'spark' ? 'var(--shadow-sm)' : 'none',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...sizeStyles[size],
    ...variantStyles[variant],
  };
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    iconLeft,
    iconRight,
    fullWidth,
    style,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-sans)',
    fontWeight: 600,
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 220ms var(--ease-standard), color 220ms var(--ease-standard), box-shadow 220ms var(--ease-standard), transform 120ms var(--ease-standard)',
    boxShadow: variant === 'primary' || variant === 'spark' ? 'var(--shadow-sm)' : 'none',
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : undefined,
    opacity: disabled ? 0.5 : 1,
    ...sizeStyles[size],
    ...variantStyles[variant],
    ...style,
  };

  return (
    <button ref={ref} type={type} disabled={disabled} style={base} {...rest}>
      {iconLeft && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{iconLeft}</span>}
      {children}
      {iconRight && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{iconRight}</span>}
    </button>
  );
});

export default Button;
